import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, IconButton, Stack, Typography } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { employerVerificationDashboardApi } from '../api/employerVerificationDashboardApi';
import { messageFromEmployerVerificationHttpError } from '../lib/employerVerificationHttpErrorMessage';
import { dossierHints } from '../lib/employerVerificationCopy';
import { CompanyDossierEvidenceBody } from '../components/verification/CompanyDossierEvidenceBody';

export function CompanyDossierPage() {
  const { verificationId } = useParams<{ verificationId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const dossierQuery = useQuery({
    queryKey: ['companyDossier', verificationId],
    queryFn: () => employerVerificationDashboardApi.companyDossier(verificationId!),
    enabled: !!verificationId?.trim(),
  });

  const d = dossierQuery.data;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: { xs: 2, md: 3 }, px: { xs: 1.5, sm: 2 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton component={RouterLink} to="/employer-verification" aria-label="Back to verification dashboard">
          <ArrowBackRoundedIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.02 }}>
            Company dossier
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.55 }}>
            {dossierHints.subtitleLine}
            {d?.uiMeta?.refreshStrategy ? (
              <>
                <br />
                <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.8125rem' }}>
                  ({d.uiMeta.refreshStrategy})
                </Box>
              </>
            ) : null}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshRoundedIcon />}
          disabled={!verificationId || dossierQuery.isFetching}
          onClick={() => qc.invalidateQueries({ queryKey: ['companyDossier', verificationId] })}
        >
          Refresh
        </Button>
      </Stack>

      {dossierQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {messageFromEmployerVerificationHttpError(dossierQuery.error)}
        </Alert>
      ) : null}

      {dossierQuery.isPending ? (
        <Typography color="text.secondary">Loading dossier…</Typography>
      ) : d ? (
        <CompanyDossierEvidenceBody d={d} variant="page" onBackToOverview={() => navigate('/employer-verification')} />
      ) : (
        <Alert severity="warning">Missing verification id.</Alert>
      )}
    </Box>
  );
}
